import { FC, memo, RefObject, useEffect, useRef, useState } from 'react'
import Tippy, { TippyProps } from '@tippyjs/react/headless'
import 'tippy.js/dist/tippy.css'
import { useSpring, SpringValues } from '@react-spring/web'
import { Instance } from 'tippy.js'
import { AnimationContainer } from './styles'

type Props = {
  isPopupOpen: boolean
  shouldCloseOnClickOutside?: boolean
  children?: React.ReactElement
  render?: TippyProps['render']
  placement?: TippyProps['placement']
  reference: TippyProps['reference']
  offsetPx?: number
}

type AnimationStyles = {
  opacity: number
  transform: string
}

type RenderHookProps = {
  instance?: Instance
  containerRef: RefObject<HTMLDivElement>
  children: React.ReactNode
  style: SpringValues<AnimationStyles>
}

const defaultOffsetPx = 16

const AnimationContainerHOC = (props: RenderHookProps) => {
  const { instance, containerRef, ...rest } = props

  useEffect(() => {
    if (containerRef.current) {
      const observer = new ResizeObserver(() => {
        instance?.popperInstance?.update()
      })
      observer.observe(containerRef.current)
      return () => observer.disconnect()
    }
  }, [containerRef, instance])

  return <AnimationContainer ref={containerRef} {...rest} />
}

export const AiSuggestionsTippyPopup: FC<Props> = memo(props => {
  const {
    isPopupOpen = false,
    children,
    render,
    placement,
    reference,
    offsetPx = defaultOffsetPx,
  } = props
  const containerRef = useRef<HTMLDivElement>(null)
  const animationConfig = { duration: 150 }
  const animationFrom = { opacity: 0, transform: 'scale(0.5)' }
  const animationTo = { opacity: 1, transform: 'scale(1)' }
  const [animationProps, setAnimationProps] = useSpring(() => animationFrom)
  const [isVisible, setIsVisible] = useState(false)

  const onShown = () => {
    if (!containerRef.current) return

    const { top, bottom } = containerRef.current.getBoundingClientRect()

    if (top < 0 || bottom > window.innerHeight) {
      containerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  const onMount = () => {
    setAnimationProps({
      ...animationTo,
      onRest: onShown,
      config: animationConfig,
    })
  }

  const onHide = ({ unmount }: { unmount: () => void }) => {
    setAnimationProps({
      ...animationFrom,
      onRest: unmount,
      config: { ...animationConfig, clamp: true },
    })
  }

  // this needed to properly update popup position if container with button can move
  // (for example during hover animation)
  useEffect(() => {
    if (isPopupOpen) {
      const timeout = setTimeout(() => setIsVisible(true), 100)
      return () => clearTimeout(timeout)
    } else {
      setIsVisible(false)
    }
  }, [isPopupOpen])

  return (
    <Tippy
      placement={placement}
      render={(attrs, content, instance) => {
        return (
          <AnimationContainerHOC
            containerRef={containerRef}
            style={animationProps}
            instance={instance}
            {...attrs}
          >
            {render?.(attrs, content, instance)}
          </AnimationContainerHOC>
        )
      }}
      visible={isVisible}
      interactive
      animation
      offset={[0, offsetPx]}
      popperOptions={{
        modifiers: [{ name: 'flip', enabled: false }],
      }}
      onMount={onMount}
      onHide={onHide}
      reference={reference}
      appendTo={document.body}
      sticky={false}
    >
      {children}
    </Tippy>
  )
})
